import { useState } from 'react';
import { useSystemSettings, useUpdateSystemSetting } from '../hooks/useSystemSettings';
import type { SystemSetting } from '../services/systemSettingsApi';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { accountingApi } from '@/features/accounting/services/accountingApi';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ValueTypeLabel: Record<string, string> = {
  percent: '%',
  number: '#',
  boolean: 'on/off',
  string: 'text',
  account: 'GL account',
};

const SettingValueInput = ({
  setting,
  onSave,
  onCancel,
}: {
  setting: SystemSetting;
  onSave: (value: string) => void;
  onCancel: () => void;
}) => {
  const [draft, setDraft] = useState(setting.value ?? '');
  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts', 'postable'],
    queryFn: accountingApi.getPostableAccounts,
    enabled: setting.valueType === 'account',
  });

  if (setting.valueType === 'account') {
    return (
      <div className="flex min-w-0 items-center justify-end gap-2">
        <Select value={draft} onValueChange={setDraft}>
          <SelectTrigger className="h-8 w-full max-w-72"><SelectValue placeholder="Select GL account" /></SelectTrigger>
          <SelectContent>
            {accounts.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                {account.accountCode} — {account.accountName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!draft} onClick={() => onSave(draft)}>
          <Check className="w-3 h-3 text-green-600" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onCancel}>
          <X className="w-3 h-3 text-red-500" />
        </Button>
      </div>
    );
  }

  if (setting.valueType === 'boolean') {
    const bool = draft === 'true';
    return (
      <div className="flex items-center justify-end gap-2">
        <Button
          size="sm"
          variant={bool ? 'default' : 'outline'}
          onClick={() => setDraft('true')}
          className="h-7 text-xs"
        >
          Yes
        </Button>
        <Button
          size="sm"
          variant={!bool ? 'default' : 'outline'}
          onClick={() => setDraft('false')}
          className="h-7 text-xs"
        >
          No
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onSave(draft)}>
          <Check className="w-3 h-3 text-green-600" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onCancel}>
          <X className="w-3 h-3 text-red-500" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <Input
        className="h-7 text-sm w-36"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        type={setting.valueType === 'percent' || setting.valueType === 'number' ? 'number' : 'text'}
        step={setting.valueType === 'percent' ? '0.1' : '1'}
        min={0}
      />
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onSave(draft)}>
        <Check className="w-3 h-3 text-green-600" />
      </Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onCancel}>
        <X className="w-3 h-3 text-red-500" />
      </Button>
    </div>
  );
};

export const SystemSettingsPage = () => {
  const { data: settings, isLoading, error } = useSystemSettings();
  const updateMutation = useUpdateSystemSetting();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const { data: postingAccounts = [] } = useQuery({
    queryKey: ['accounts', 'postable'],
    queryFn: accountingApi.getPostableAccounts,
  });

  if (isLoading) return <LoadingAnimation fullScreen />;
  if (error)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load system settings</p>
      </div>
    );

  const grouped = (settings ?? []).reduce<Record<string, SystemSetting[]>>((acc, s) => {
    if (!acc[s.group]) acc[s.group] = [];
    acc[s.group].push(s);
    return acc;
  }, {});

  const handleSave = (key: string, value: string) => {
    updateMutation.mutate(
      { key, value },
      {
        onSuccess: () => {
          toast.success('Setting updated successfully');
          setEditingKey(null);
        },
        onError: () => {
          toast.error('Failed to update setting');
        },
      }
    );
  };

  return (
    <div className="min-w-0 max-w-full space-y-6 overflow-x-hidden overflow-y-auto pb-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">System Settings</h1>
        <p className="text-gray-500 mt-1">
          Configure system-wide settings without requiring a code deployment.
        </p>
      </div>

      {Object.entries(grouped).map(([group, items]) => (
        <div key={group} className="min-w-0 overflow-hidden rounded-lg border bg-card">
          <div className="px-4 py-3 border-b bg-muted/50">
            <h2 className="font-semibold text-sm">{group}</h2>
          </div>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[58%]">Setting</TableHead>
                <TableHead className="w-[26%] text-right">Value</TableHead>
                <TableHead className="w-24 text-right">Type</TableHead>
                <TableHead className="w-16 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.key}>
                  <TableCell className="whitespace-normal break-words">
                    <div className="text-sm font-medium">{s.label}</div>
                    {s.description && (
                      <div className="mt-0.5 text-xs leading-snug text-muted-foreground">{s.description}</div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words text-right">
                    {editingKey === s.key ? (
                      <SettingValueInput
                        setting={s}
                        onSave={(v) => handleSave(s.key, v)}
                        onCancel={() => setEditingKey(null)}
                      />
                    ) : (
                      <span className="break-words font-mono text-sm">
                        {s.valueType === 'boolean'
                          ? s.value === 'true'
                            ? 'Yes'
                            : 'No'
                          : s.valueType === 'percent'
                          ? `${s.value}%`
                          : s.valueType === 'account'
                          ? (() => {
                              const account = postingAccounts.find((a) => a.id === s.value);
                              return account ? `${account.accountCode} — ${account.accountName}` : 'Not configured';
                            })()
                          : s.value ?? '—'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className="text-xs">
                      {ValueTypeLabel[s.valueType] ?? s.valueType}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {editingKey !== s.key && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => setEditingKey(s.key)}
                        disabled={!s.isActive}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}

      {settings?.length === 0 && (
        <p className="text-sm text-muted-foreground">No settings found.</p>
      )}
    </div>
  );
};
